import type { Rect } from '../core/types';
import { RenderTarget } from '../gl/renderTarget';
import type { Texture } from '../gl/texture';

/**
 * Pixel storage for one layer. The MVP uses a single document-sized texture; the
 * interface exists so a tiled implementation can be added later without touching
 * the tools, which only ever go through these five members.
 */
export interface LayerSurface {
  readonly width: number;
  readonly height: number;
  /** Texture handed to the compositor as the layer's source. */
  readonly texture: Texture;
  /** Framebuffer that renders into the surface. */
  readonly target: RenderTarget;
  readPixels(rect?: Rect): Uint8Array;
  writePixels(rect: Rect, data: ArrayBufferView): void;
  resize(width: number, height: number): void;
  readonly bytes: number;
  dispose(): void;
}

export class TextureSurface implements LayerSurface {
  readonly target: RenderTarget;

  constructor(gl: WebGL2RenderingContext, width: number, height: number) {
    this.target = new RenderTarget(gl, width, height, 'rgba8', 'linear');
    this.target.clear(0, 0, 0, 0);
  }

  get texture(): Texture {
    return this.target.texture;
  }

  get width(): number {
    return this.target.width;
  }

  get height(): number {
    return this.target.height;
  }

  get bytes(): number {
    return this.target.bytes;
  }

  readPixels(rect?: Rect): Uint8Array {
    return this.target.texture.readPixels(rect);
  }

  writePixels(rect: Rect, data: ArrayBufferView): void {
    this.target.texture.uploadPixels(rect, data);
  }

  resize(width: number, height: number): void {
    this.target.resize(width, height);
  }

  dispose(): void {
    this.target.dispose();
  }
}
